import { useState, useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import { Button, TextInput, Textarea } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import Modal from '@components/ui/Modal/Modal';
import FullPageStepper from '@components/shared/FullPageStepper/FullPageStepper';
import type { FullPageStepperStep } from '@components/shared/FullPageStepper/FullPageStepper';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import {
  getFormTemplateById,
  createFormTemplate,
  updateFormTemplate,
} from '@/services/formTemplateService';
import { getFormConfigs } from '@/services/formService';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useCountries } from '@/hooks/useCountries';
import useMultiStepForm from '@/hooks/useMultiStepForm';
import { uniqueId } from '@utils/uniqueId';
import { toDraggableTabItems, getRemovedTabDetails } from '@utils/tabHelpers';
import EntityTabContentEditor from '@features/entity-templates/EntityTabContentEditor';
import type { EntityTabWithClientIds } from '@features/entity-templates/types';
import type { FormTemplateTab } from '@/types/formTemplate';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './add.module.scss';

// ============================================
// ROUTE
// ============================================

interface EntityTemplateAddSearch {
  id?: string;
}

export const Route = createFileRoute('/w/entity-templates/add')({
  staticData: { hideShell: true },
  validateSearch: (search: Record<string, unknown>): EntityTemplateAddSearch => ({
    id: typeof search.id === 'string' ? search.id : undefined,
  }),
  component: EntityTemplateAddPage,
});

// ============================================
// HELPERS
// ============================================

function toClientTabs(apiTabs: FormTemplateTab[]): EntityTabWithClientIds[] {
  return apiTabs.map((tab) => ({
    ...tab,
    description: tab.description ?? '',
    _tabClientId: uniqueId('tab'),
    forms: (tab.forms ?? []).map((form) => ({
      ...form,
      filters: form.filters ?? {
        country: { matchAll: false, values: [] },
        productType: { matchAll: true, values: [] },
      },
      attributes: form.attributes ?? { isDefault: false, isMandatory: false },
      _clientId: uniqueId('form'),
    })),
  }));
}

function toApiTabs(clientTabs: EntityTabWithClientIds[]): FormTemplateTab[] {
  return clientTabs.map((tab, i) => ({
    name: tab.name,
    description: tab.description,
    order: i + 1,
    forms: tab.forms.map((form, fi) => ({
      formConfigId: form.formConfigId,
      formName: form.formName,
      order: fi + 1,
      accessLevel: form.accessLevel,
      accessOverrides: form.accessOverrides,
      filters: form.filters,
      attributes: form.attributes,
    })),
  }));
}

function createEmptyTab(): EntityTabWithClientIds {
  return {
    _tabClientId: uniqueId('tab'),
    name: '',
    description: '',
    order: 0,
    forms: [],
  };
}

// ============================================
// STEP 1 FORM
// ============================================

interface Step1FormValues {
  name: string;
  description: string;
  countryId: string;
}

// ============================================
// COMPONENT
// ============================================

function EntityTemplateAddPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // --- Edit mode detection ---
  const { id: templateId } = Route.useSearch();
  const isEditMode = !!templateId;

  // --- Template query (edit mode only) ---
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ['formTemplate', templateId],
    queryFn: () => getFormTemplateById(templateId!),
    enabled: isEditMode,
  });

  // --- Reference data ---
  const { countries, isLoading: countriesLoading } = useCountries();

  const countryOptions = useMemo(
    () => countries.map((c) => ({ label: c.name, value: c.id })),
    [countries],
  );

  const { data: formConfigsData, refetch: refetchFormConfigs, error: formConfigsError } = useQuery({
    queryKey: ['formConfigs', { size: 500 }],
    queryFn: () => getFormConfigs({ size: 500 }),
  });

  const formConfigs = formConfigsData?.content ?? [];

  // --- Step 1 form ---
  const {
    control,
    register,
    trigger,
    getValues,
    reset: resetForm,
    formState: { errors },
  } = useForm<Step1FormValues>({
    defaultValues: { name: '', description: '', countryId: '' },
  });

  // Pre-fill Step 1 in edit mode
  useEffect(() => {
    if (template) {
      resetForm({
        name: template.name ?? '',
        description: template.description ?? '',
        countryId: template.countryId ?? '',
      });
    }
  }, [template, resetForm]);

  // --- Multi-step form ---
  const stepper = useMultiStepForm({
    totalSteps: 2,
    onValidateStep: async (stepIndex) => {
      if (stepIndex === 0) {
        return trigger(['name', 'countryId']);
      }
      return true;
    },
  });

  // --- Steps config ---
  const steps: FullPageStepperStep[] = useMemo(
    () => [
      {
        id: 'details',
        label: t('K_STEP_TEMPLATE_DETAILS', 'Template Details'),
        title: t('K_STEP_TEMPLATE_DETAILS', 'Template Details'),
        description: t(
          'K_STEP_ENTITY_TEMPLATE_DETAILS_DESC',
          'Select the country for this entity template',
        ),
      },
      {
        id: 'configuration',
        label: t('K_STEP_TEMPLATE_CONFIGURATION', 'Template Configuration'),
        title: t('K_STEP_TEMPLATE_CONFIGURATION', 'Template Configuration'),
        description: t(
          'K_STEP_TEMPLATE_CONFIGURATION_DESC',
          'Configure tabs, forms, and settings',
        ),
      },
    ],
    [t],
  );

  // --- Template editor state (Step 2) ---
  const [tabs, setTabs] = useState<EntityTabWithClientIds[]>([]);
  const [, setSavedTabsJson] = useState('');
  const [activeTabId, setActiveTabId] = useState('');
  const [removedTabIds, setRemovedTabIds] = useState<Set<string>>(new Set());
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [tabNameErrors, setTabNameErrors] = useState<Map<string, string>>(new Map());

  // Sync template data (edit mode)
  const [synced, setSynced] = useState(false);
  useEffect(() => {
    if (template && !synced) {
      setSynced(true);
      const clientTabs = toClientTabs(template.tabs);
      setTabs(clientTabs);
      setSavedTabsJson(JSON.stringify(toApiTabs(clientTabs)));
      if (clientTabs[0]) {
        setActiveTabId(clientTabs[0]._tabClientId);
      }
    }
  }, [template, synced]);

  const liveTabs = useMemo(
    () => tabs.filter((tab) => !removedTabIds.has(tab._tabClientId)),
    [tabs, removedTabIds],
  );

  const removedTabDetails = useMemo(
    () => getRemovedTabDetails(tabs, removedTabIds),
    [tabs, removedTabIds],
  );

  const activeTabIndex = tabs.findIndex((tab) => tab._tabClientId === activeTabId);
  const activeTab = activeTabIndex >= 0 ? tabs[activeTabIndex] : undefined;
  const draggableItems = useMemo(() => toDraggableTabItems(tabs), [tabs]);

  // --- Mutations ---
  const createMutation = useMutation({ mutationFn: createFormTemplate });
  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string; tabs: FormTemplateTab[] }) =>
      updateFormTemplate(templateId!, { scope: 'LEGAL_ENTITY', ...data }),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // --- Navigation ---
  const handleClose = useCallback(() => {
    void navigate({ to: '/w/entity-templates' });
  }, [navigate]);

  // --- Tab handlers ---
  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  const handleReorder = useCallback((reorderedItems: TabItem[]) => {
    setTabs((prev) => {
      const tabMap = new Map(prev.map((tab) => [tab._tabClientId, tab]));
      return reorderedItems
        .map((item) => tabMap.get(item.id))
        .filter((tab): tab is EntityTabWithClientIds => tab !== undefined);
    });
  }, []);

  const handleAddTab = useCallback(() => {
    const newTab = createEmptyTab();
    newTab.name = t('K_DEFAULT_TAB_NAME', 'New Tab {number}').replace('{number}', String(liveTabs.length + 1));
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newTab._tabClientId);
  }, [liveTabs.length]);

  const handleTabChange = useCallback((tabIndex: number, updated: EntityTabWithClientIds) => {
    setTabs((prev) => {
      const next = [...prev];
      next[tabIndex] = updated;
      return next;
    });
    if (updated.name.trim()) {
      setTabNameErrors((prev) => {
        if (!prev.has(updated._tabClientId)) return prev;
        const next = new Map(prev);
        next.delete(updated._tabClientId);
        return next;
      });
    }
  }, []);

  const handleRemoveTab = useCallback(
    (tab: TabItem) => {
      setRemovedTabIds((prev) => new Set(prev).add(tab.id));
      if (tab.id === activeTabId) {
        const currentIndex = tabs.findIndex((t) => t._tabClientId === tab.id);
        const remaining = tabs.filter(
          (t, i) =>
            !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id && i < currentIndex,
        );
        const fallback =
          remaining[remaining.length - 1] ??
          tabs.find((t) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id);
        setActiveTabId(fallback?._tabClientId ?? '');
      }
    },
    [activeTabId, tabs, removedTabIds],
  );

  const handleUndoRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => {
      const next = new Set(prev);
      next.delete(tab.id);
      return next;
    });
  }, []);

  // --- Validation & Save ---
  const validateTabs = useCallback((): boolean => {
    const nameErrors = new Map<string, string>();
    let firstErrorTabId: string | undefined;

    for (const tab of liveTabs) {
      if (!tab.name.trim()) {
        nameErrors.set(
          tab._tabClientId,
          t('K_VALIDATION_TAB_NAME_REQUIRED', 'Tab name is required'),
        );
        if (!firstErrorTabId) firstErrorTabId = tab._tabClientId;
      }
    }

    setTabNameErrors(nameErrors);

    if (nameErrors.size > 0) {
      if (firstErrorTabId) setActiveTabId(firstErrorTabId);
      return false;
    }
    return true;
  }, [liveTabs, t]);

  const performSave = useCallback(() => {
    const tabsToSave = tabs.filter((tab) => !removedTabIds.has(tab._tabClientId));
    const apiTabs = toApiTabs(tabsToSave);

    const onSuccess = () => {
      void queryClient.invalidateQueries({ queryKey: ['formTemplates', 'LEGAL_ENTITY'] });
      void navigate({ to: '/w/entity-templates' });
    };

    const onError = (error: unknown) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    };

    const { name, description, countryId } = getValues();

    if (isEditMode) {
      updateMutation.mutate({ name, description, tabs: apiTabs }, { onSuccess, onError });
    } else {
      createMutation.mutate(
        { scope: 'LEGAL_ENTITY', name, description, countryId, tabs: apiTabs },
        { onSuccess, onError },
      );
    }
  }, [
    tabs,
    removedTabIds,
    isEditMode,
    templateId,
    createMutation,
    updateMutation,
    queryClient,
    navigate,
    getValues,
    t,
    addNotification,
  ]);

  const handleSaveClick = useCallback(() => {
    if (!validateTabs()) return;
    if (removedTabIds.size > 0) {
      setShowSaveConfirm(true);
    } else {
      performSave();
    }
  }, [removedTabIds.size, performSave, validateTabs]);

  const handleConfirmSave = useCallback(() => {
    setShowSaveConfirm(false);
    performSave();
  }, [performSave]);

  // --- Footer ---
  const footer = useMemo(() => {
    if (stepper.currentStep === 0) {
      return (
        <div className={styles.footer}>
          <Button variant="outline" size="md" onClick={handleClose}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button variant="filled" size="md" onClick={() => void stepper.goToNext()}>
            {t('K_BUTTON_NEXT', 'Next')}
          </Button>
        </div>
      );
    }
    return (
      <div className={styles.footer}>
        <Button variant="outline" size="md" onClick={stepper.goToPrev}>
          {t('K_BUTTON_BACK', 'Back')}
        </Button>
        <Button variant="filled" size="md" onClick={handleSaveClick} disabled={isSaving}>
          {isSaving ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
        </Button>
      </div>
    );
  }, [stepper, handleClose, handleSaveClick, isSaving, t]);

  // --- Forbidden (page-swap — Step 2 form picker is meaningless without
  //     the form-configs list).
  if (isForbiddenError(formConfigsError)) {
    return (
      <FullPageStepper
        pageTitle={
          isEditMode
            ? t('K_PAGE_TITLE_EDIT_ENTITY_TEMPLATE', 'Edit Entity Template')
            : t('K_PAGE_TITLE_CREATE_ENTITY_TEMPLATE', 'Create Entity Template')
        }
        steps={steps}
        activeStep={0}
        onClose={handleClose}
        contentMaxWidth="100%"
      >
        <Forbidden
          variant="page"
          onAction={() => void navigate({ to: '/w/entity-templates' })}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </FullPageStepper>
    );
  }

  // --- Loading state (edit mode) ---
  if (isEditMode && templateLoading) {
    return (
      <FullPageStepper
        pageTitle={t('K_PAGE_TITLE_EDIT_ENTITY_TEMPLATE', 'Edit Entity Template')}
        steps={steps}
        activeStep={0}
        onClose={handleClose}
        contentMaxWidth="100%"
      >
        <p>{t('K_LOADING', 'Loading...')}</p>
      </FullPageStepper>
    );
  }

  return (
    <FullPageStepper
      pageTitle={
        isEditMode
          ? t('K_PAGE_TITLE_EDIT_ENTITY_TEMPLATE', 'Edit Entity Template')
          : t('K_PAGE_TITLE_CREATE_ENTITY_TEMPLATE', 'Create Entity Template')
      }
      steps={steps}
      activeStep={stepper.currentStep}
      onClose={handleClose}
      onStepClick={(stepIndex: number) => stepper.goToStep(stepIndex)}
      footer={footer}
      contentMaxWidth="100%"
    >
      {/* ---- Step 1: Template Details ---- */}
      {stepper.currentStep === 0 && (
        <div className={styles.step1Content}>
          <div className={styles.fieldGroup}>
            <TextInput
              label={t('K_LABEL_TEMPLATE_NAME', 'Template Name')}
              placeholder={t('K_PLACEHOLDER_TEMPLATE_NAME', 'Enter template name')}
              error={errors.name?.message}
              {...register('name', {
                required: t('K_VALIDATION_TEMPLATE_NAME_REQUIRED', 'Template name is required'),
                maxLength: {
                  value: 255,
                  message: t('K_VALIDATION_MAX_255', 'Max 255 characters'),
                },
              })}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Textarea
              label={t('K_LABEL_TEMPLATE_DESCRIPTION', 'Description')}
              placeholder={t('K_PLACEHOLDER_TEMPLATE_DESCRIPTION', 'Enter template description')}
              {...register('description', {
                maxLength: {
                  value: 500,
                  message: t('K_VALIDATION_MAX_500', 'Max 500 characters'),
                },
              })}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Controller
              name="countryId"
              control={control}
              rules={{
                required: t('K_VALIDATION_COUNTRY_REQUIRED', 'Country is required'),
              }}
              render={({ field }) => (
                <Select
                  label={t('K_LABEL_COUNTRY', 'Country')}
                  options={countryOptions}
                  searchable
                  placeholder={t('K_PLACEHOLDER_SELECT_COUNTRY', 'Select a country')}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.countryId?.message}
                  disabled={isEditMode || countriesLoading}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* ---- Step 2: Template Configuration ---- */}
      {stepper.currentStep === 1 && (
        <>
          {/* Toolbar */}
          <div className={styles.toolbar}>
            <Button variant="outline" size="sm" onClick={handleAddTab}>
              {t('K_BUTTON_ADD_TAB', '+ Add Tab')}
            </Button>
          </div>

          {/* Tabs */}
          <DraggableTabs
            tabs={draggableItems}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onReorder={handleReorder}
            removable
            removedTabIds={removedTabIds}
            onRemove={handleRemoveTab}
            onUndoRemove={handleUndoRemoveTab}
          />

          {/* Tab content */}
          <div className={styles.tabContent}>
            {activeTab && !removedTabIds.has(activeTab._tabClientId) ? (
              <EntityTabContentEditor
                tab={activeTab}
                tabIndex={activeTabIndex}
                onTabChange={handleTabChange}
                formConfigs={formConfigs}
                onFormSelectorFocus={refetchFormConfigs}
                tabNameError={tabNameErrors.get(activeTab._tabClientId)}
              />
            ) : (
              <p className={styles.tabContentPlaceholder}>
                {tabs.length === 0
                  ? t('K_MESSAGE_ADD_TAB_GET_STARTED', 'Click "+ Add Tab" to get started.')
                  : t('K_MESSAGE_SELECT_TAB_EDIT', 'Select a tab to edit its content.')}
              </p>
            )}
          </div>

          {/* Save confirmation modal */}
          <Modal
            isOpen={showSaveConfirm}
            onClose={() => setShowSaveConfirm(false)}
            title={t('K_MODAL_CONFIRM_CHANGES', 'Confirm Changes')}
            size="sm"
            footer={
              <div className={styles.confirmFooter}>
                <Button variant="outline" size="sm" onClick={() => setShowSaveConfirm(false)}>
                  {t('K_CANCEL', 'Cancel')}
                </Button>
                <Button variant="filled" size="sm" onClick={handleConfirmSave} disabled={isSaving}>
                  {isSaving ? t('K_BUTTON_SAVING', 'Saving...') : t('K_CONFIRM', 'Confirm')}
                </Button>
              </div>
            }
          >
            <div className={styles.confirmBody}>
              <p>
                {t(
                  'K_MESSAGE_TABS_PERMANENTLY_REMOVED',
                  'The following tabs will be permanently removed:',
                )}
              </p>
              <ul className={styles.confirmList}>
                {removedTabDetails.map((tab, i) => (
                  <li key={i}>
                    {tab.name}
                    {tab.formNames && ` - ${tab.formNames}`}
                  </li>
                ))}
              </ul>
            </div>
          </Modal>
        </>
      )}
    </FullPageStepper>
  );
}
